// @givmo/board — every word the Form 990 page says in Board's own voice (CBG-YR5-VIEW; the derivation
// §2, frames p1, p2, p3 and p9, and the states no frame drew).
//
// Every line is approved in copy round CBG-YR5-COPY1's first part (Jonathan, 2026-10-16 07:38 PDT,
// "go with your recs"; `scratchpad/2026-10-15-viewweb-copy-round/copy-review.html`), with both of
// its choices as recommended (line 8a's minutes, line 12c's words), and lines 15a and 15b's
// (CBG-YR5-VIEW-15) in its next part (Jonathan, 2026-10-16 11:54 PDT, "go with your recs";
// `scratchpad/2026-10-16-distweb-copy-round/copy-review.html`), their facts as built, and the two
// links to the Schedule O draft (CBG-YR5-SCHO) in its next part after that (Jonathan, 2026-10-16
// 19:35 PDT, "go with your recs"; `scratchpad/2026-10-16-scho-copy-round/copy-review.html`). Line
// 8a's written-consent column, "Dated", is CBG-FIX88's word (Jonathan, in-chat 2026-10-16 20:54
// PDT, "yes to both"). Line 8b's two committee lines (CBG-YR5-VIEW-8B: a committee's "by the
// members who were there" and its line when Board holds nothing of it in the year) are approved as
// built in the round's next part after that, its three notes as recommended (Jonathan, in-chat
// 2026-10-17 06:54 PDT, "html mocks approved.", then 06:55 PDT, "go with your recs";
// `scratchpad/2026-10-16-view8b-copy-round/copy-review.html`). A line added since is marked DRAFT
// where it sits, until its own round. The other marks, each where its line sits:
//   * SAFE — the registered safe phrase "Evidence supporting your Part VI answers";
//   * APPROVED — reused word for word from a shipped screen, which each names.
//
// ⚠ THE IRS'S WORDS ARE NOT HERE, AND NEVER WILL BE (D-CBG-30). Each question, its sub-lines, the
// sentences the form prints with them and each instruction excerpt with its citation arrive in the
// read, from the backend's line map, and the page shows them as quotations with their source. This file
// is Board's voice only, so it keeps every ban: no answer, no check, no score, and none of the words
// met, satisfies, complete, ready, readiness, compliant, correct, on time, late, covered, missing, needs
// or should (`form990-words.test.ts`).
//
// One departure from the derivation's draft, for the copy round: "…recorded in Compliance" reads
// "…recorded in Givmo", because the module's name is a bare banned root on Board's string plane.

/** A count with its noun: "1 meeting", "4 meetings". */
function counted(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "2026", "2026 and 2027", "2025, 2026 and 2027" — the cycles' own labels, as the year's name. */
export function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export const form990Words = {
  // ── The page (frame p1) ──────────────────────────────────────────────────────────────────────────
  pageTitle: 'Form 990',
  // The page's first part, beside "Compensation decisions" (approved, reused: CBG-YR4-COPY1's tab
  // word), the part that moved here (D-CBG-35 (1)). Not drawn at the design stop; approved in the
  // wayfinding copy round (2026-10-24 11:11 PDT).
  partViTab: 'Part VI',
  /** SAFE ("Evidence supporting your Part VI answers"), then approved. Above both tabs, it carries
   *  the Compensation decisions tab's "not a conclusion about them" too, the tab's own near-copy gone
   *  (the wayfinding copy round's 7.5): Board's informational-never-counsel posture (plan §3.2).
   *  CBG-UX-COPY2 (A4): "about the answers", so "them" never reads as the records. */
  pageDescription:
    'Evidence supporting your Part VI answers, from your board’s own records, not a conclusion about the answers.',
  howLead: [
    'Each line shows what your board recorded that bears on that question, with links to the records. A dash means Board keeps no record for it.',
    'Your organization answers each question on its return. Board doesn’t.',
  ],
  howRecorded: 'The tax year comes from the fiscal year end your organization recorded in Givmo.',
  howChosen:
    'Your organization’s fiscal year end isn’t recorded in Givmo, so the tax years come from the day you choose here. It’s kept only in this page’s address.',
  formNote: (formYear: number) => `Questions as they appear on Form 990 (${formYear}), Part VI.`,
  /** Shown under the year when the questions are an earlier form's (contract §2.3). */
  formDiffers: (formYear: number) => `Questions as they appear on Form 990 (${formYear}).`,
  inYourRecords: 'In your board’s records',

  // ── The year ─────────────────────────────────────────────────────────────────────────────────────
  yearLabel: 'Tax year',
  /** "2026 Form 990 · tax year Jan 1 – Dec 31, 2026", the period's dates as the select shows them. */
  yearOption: (namedYear: number, period: string, short: boolean) =>
    `${namedYear} Form 990 · ${short ? 'short tax year' : 'tax year'} ${period}`,
  notEnded: (endsOn: string) =>
    `This tax year ends ${endsOn}. Each line shows what your board has recorded so far.`,
  /** The years taken from the day chosen, never a recorded year end (contract §3.1). */
  chosenLine: (monthDay: string) =>
    `Tax years ending ${monthDay}, the day you chose. Your organization’s fiscal year end isn’t recorded in Givmo.`,
  changeDay: 'Change the day',
  noYearEnd:
    'Your organization’s fiscal year end isn’t recorded in Givmo, so choose the day your tax year ends.',
  monthLabel: 'Month',
  dayLabel: 'Day',
  monthPlaceholder: 'Month',
  dayPlaceholder: 'Day',
  showYears: 'Show the tax years',
  cancel: 'Cancel',
  /** Only where a refusal carries no sentence of its own; the served line is shown when it does. */
  yearEndRefused: 'Choose the month and the day your tax year ends.',
  /** Likewise, for an address naming a tax year the span doesn't hold. */
  taxYearRefused: 'Choose one of the tax years listed.',

  // ── The page's other answers ─────────────────────────────────────────────────────────────────────
  /** Only where the type's refusal carries no sentence of its own (the served 409's is shown). */
  typeRefused: 'This organization’s type has no Form 990 page.',
  notServed: 'The Form 990 page isn’t available yet.',
  loadFailed: 'We couldn’t load the Form 990 page just now.',
  /** APPROVED (Evaluations, `assessment-words.ts`). */
  tryAgain: 'Try again',

  // ── A line, opened (frame p9) ────────────────────────────────────────────────────────────────────
  nothingRecorded: 'Board keeps no record that bears on this question.',
  /** APPROVED (CBG-YR6-COPY1, the line-8b dialog, `committees-words.ts`). */
  whatInstructionsSay: 'What the instructions say',
  // Not drawn (CBG-YR5-SCHO): line 11b opened, the way to its Schedule O draft.
  scheduleODraft: 'Schedule O draft',
  // Not drawn (CBG-YR5-SCHO): under line 8b's no-committee quotations (frame p3).
  scheduleODraftFor8b: 'Schedule O draft for line 8b',

  // ── Line 1a ──────────────────────────────────────────────────────────────────────────────────────
  /**
   * The served day, whichever year: the year's last day, or for a year not ended the day the read was
   * served on (contract C11). Never "today": that day is the server's, and the reader's may differ.
   */
  directorsSummary: (day: string, count: number) => `Recorded as directors on ${day}: ${count}`,
  /** No seat listed on the day, and several committees your organization answered "broad". */
  committeesAnsweredBroad: (names: readonly string[]) =>
    `${joinLabels(names)}: yes, broad authority (your answers)`,
  /** A seat listed by the organization's recorded term, before Board held it (contract §4.3.1). */
  fromRecordedTerm: 'From the term your organization recorded',
  fromRecordedTerms: 'Listed from the terms your organization recorded.',
  /** APPROVED (`committees-words.ts`). */
  notADirector: 'Not a director',
  /** APPROVED (`committees-words.ts`). */
  roleChair: 'Chair',
  /** APPROVED (`committees-words.ts`). */
  until: (day: string) => `Until ${day}`,

  // ── Lines 1b, 2 and 12b ──────────────────────────────────────────────────────────────────────────
  // CBG-UX-COPY2's amendment (D-CBG-42): the year's set is the "conflicts of interest forms".
  answersIn: (labels: readonly string[]) =>
    `Answers in the ${joinLabels(labels)} conflicts of interest forms`,
  disclosures: (labels: readonly string[]) =>
    `The ${joinLabels(labels)} conflicts of interest forms`,
  eachAnswered: 'Each person answered for themselves. Board doesn’t combine the answers.',
  /** APPROVED (`coi-words.ts`, `yearTitle`), in full here: the short "{label} forms" is the
   *  Conflicts of interest page's own (CBG-UX-COPY2's amendment). */
  cycleTitle: (label: string) => `${label} conflicts of interest forms`,
  askedAbout: (period: string) => `Asked about ${period}`,
  openedOn: (day: string) => `Opened ${day}`,
  closedOn: (day: string) => `Closed ${day}`,
  /** APPROVED (`coi-words.ts`, `submittedOf` and `submittedWord`, "4 of 7 submitted"). */
  submitted: (submitted: number, total: number) => `${submitted} of ${total} submitted`,
  closeRecord: 'Its record',

  // ── Line 4 ───────────────────────────────────────────────────────────────────────────────────────
  versionAdopted: (name: string, day: string) => `${name} version adopted ${day}`,
  versionsAdopted: (count: number) =>
    `${counted(count, 'version', 'versions')} adopted in this tax year`,
  /** APPROVED (`committees-words.ts`, `charterVersion`). */
  adoptedOn: (day: string) => `Adopted ${day}`,
  adoptedBy: 'Adopted by',
  /** APPROVED (D-CBG-9, the minute book's own word for a record from before Givmo; CBG-UX-COPY2's
   *  A5b, "From earlier records", as the book's status reads). */
  imported: 'From earlier records',

  // ── Line 8a (frame p2) ───────────────────────────────────────────────────────────────────────────
  meetingsAndConsents: (meetings: number, consents: number) =>
    meetings > 0 && consents > 0
      ? `${counted(meetings, 'meeting', 'meetings')} and ${counted(consents, 'written consent', 'written consents')}`
      : meetings > 0
        ? counted(meetings, 'meeting', 'meetings')
        : counted(consents, 'written consent', 'written consents'),
  meetingsHeading: 'Meetings of the board',
  consentsHeading: 'Written consents of the board',
  colMeeting: 'Meeting',
  colHeld: 'Held',
  colMinutes: 'Minutes',
  colLine8: 'Line-8 date',
  colConsent: 'Written consent',
  /**
   * The date the consent bears, given when it is drafted (`consent_date`); it executes at its last
   * signature, which can fall days later (CBG-FIX88; the review card's "Dated {date}").
   */
  colDated: 'Dated',
  /** Approved minutes: "Approved Feb 3, 2025 · by written consent"; with no way, the day alone. */
  approvedOnWay: (day: string, way: string | null) =>
    way === null ? `Approved ${day}` : `Approved ${day} · ${way}`,
  /** The line-8 date beside a meeting or consent with no later meeting recorded: it can still move. */
  line8Open: (day: string) => `${day} (no later meeting recorded)`,
  approvedWay: (way: string | null | undefined) =>
    way === 'written_consent'
      ? 'by written consent'
      : way === 'meeting'
        ? 'at a meeting'
        : way === 'officer'
          ? 'by an officer'
          : way === 'directors_present'
            ? 'by the directors who were there'
            : null,
  /**
   * APPROVED (D-CBG-9): the minute book's own words for the same minutes' state
   * (`records-decode.ts`, `recordStateLabel`), so the two pages agree: drafted, out for approval,
   * and approved minutes the read gives no day for.
   */
  minutesDraft: 'Draft',
  minutesAwaiting: 'Awaiting approval',
  minutesApproved: 'Approved',
  /** A consent recorded with no title of its own. */
  untitledConsent: 'Written consent',

  // ── Line 8b (frames p2 and p3) ───────────────────────────────────────────────────────────────────
  /** APPROVED (CBG-YR6-COPY1, the Committees page's empty state). */
  noCommittees: 'No committees recorded',
  committeeAnswered: (name: string, answer: string) => `${name}: ${answer} (your answer)`,
  committeesAnsweredYes: (names: readonly string[]) => `${joinLabels(names)}: yes (your answers)`,
  noCommitteeAnsweredYes: 'No committee answered yes (your answers)',
  committeesNotAnswered: 'Committees recorded, no answer recorded',
  answerInSummary: (choice: string) =>
    choice === 'broad'
      ? 'yes, broad authority'
      : choice === 'particular_matters'
        ? 'yes, limited to particular matters'
        : 'no',
  /** APPROVED (CBG-YR6-COPY1, `committees-words.ts`, `answerLabel`). */
  answerLabel: (choice: string) =>
    choice === 'broad'
      ? 'Yes, broad authority'
      : choice === 'particular_matters'
        ? 'Yes, limited to particular matters'
        : 'No',
  /** APPROVED (`committees-words.ts`, `dissolvedHeader`). */
  dissolvedOn: (day: string) => `Dissolved ${day}`,
  earlierAnswer: (answer: string, citation: string, day: string | null) =>
    day === null
      ? `Earlier: ${answer} · ${citation}`
      : `Earlier: ${answer} · ${citation} · recorded ${day}`,
  // CBG-YR5-VIEW-8B, never on screen today: line 8a's completeness case ("by the
  // directors who were there") in a committee's words (the committee pages' "the members who were
  // there", `committee-meetings-words.ts`), whose members need not be directors. It answers a value
  // the read's type admits, the signatures' way, which the read never serves for a meeting's own
  // minutes, the only minutes line 8b lists (the backend's `board-form-990.md` §4.3.3: only a
  // closed session's own minutes are approved that way). A committee's other ways read as line
  // 8a's (`approvedWay`): only its own members' written consent can approve its minutes, and its
  // name heads the table.
  committeeApprovedWay: (way: string | null | undefined) =>
    way === 'directors_present' ? 'by the members who were there' : null,
  // CBG-YR5-VIEW-8B, not drawn: a committee answered yes for which the read serves
  // no meeting and no written consent in the tax year. The Schedule O page's approved words for the
  // board's (`schedule-o-words.ts`, `noMeetingsOrConsents`), for one committee: what Board holds,
  // never that the committee didn't meet.
  committeeNoneInYear:
    'Board holds no record of this committee’s meetings or written consents in this tax year.',
  /**
   * APPROVED (CBG-YR6-COPY1, frame c5): a committee's line-8 rule, word for word the backend's
   * `board_contemporaneity._COMMITTEE_RULE`, which a committee meeting's minutes clock states. The read
   * serves the board's alone (`board_meetings.rule`, under line 8a), so line 8b says the committee's
   * once, under its committees' tables (frame p2).
   */
  committeeRule:
    'The Form 990 instructions for Part VI, line 8 describe documentation as contemporaneous when it is prepared by the later of the next meeting of the committee or 60 days after the meeting or written action.',

  // ── Lines 12a, 13 and 14 ─────────────────────────────────────────────────────────────────────────
  /** Each policy by the name its question gives it. */
  policyName: (kind: string) =>
    kind === 'coi_policy'
      ? 'Conflict of interest policy'
      : kind === 'whistleblower_policy'
        ? 'Whistleblower policy'
        : 'Document retention and destruction policy',
  inForceSince: (name: string, day: string) => `${name}, in force since ${day}`,
  adoptedThisYear: (name: string, day: string) => `${name}, adopted ${day}`,
  inForceOn: (day: string) => `In force on ${day}`,
  /** A year not ended: the version adopted so far (contract C11), so no day still ahead is named. */
  inForce: 'In force',
  adoptedInYear: 'Adopted in this tax year',

  // ── Line 12c ─────────────────────────────────────────────────────────────────────────────────────
  // Composed from the meetings the read serves, and only when it serves one: with none, the line is a
  // dash. The summary names the kinds the year's minutes hold, wherever they sit, and never the
  // register, which the page doesn't read; the opened line keeps the register's link as the way
  // there. A recusal and a determination at one meeting read as both, as at two (the orchestrator's
  // ruling on the approved page's "Both", 2026-10-16).
  recusalsInMinutes: 'Recusals in the minutes',
  determinationsInMinutes: 'Determinations in the minutes',
  recusalsAndDeterminationsInMinutes: 'Recusals and determinations in the minutes',
  registerLink: 'The conflict-of-interest register',
  recusalRecorded: 'A recusal recorded',
  determinationRecorded: 'A determination recorded',

  // ── Lines 15a and 15b (CBG-YR5-VIEW-15; the backend's `board-return-review.md` §20) ──────────────
  // Every line of this section approved in CBG-YR5-COPY1's next part (2026-10-16), as built.
  // A decision is on a line only by the organization's own label; Board never answers line 15. The
  // facts name only what a decision's record holds, never what it doesn't (the orchestrator's C21):
  // no "No documents named", no "No determination recorded".
  /** "Dana Whitfield, Executive Director": whose pay, in the organization's words. */
  personAndPosition: (name: string, position: string) => `${name}, ${position}`,
  /**
   * The summary (frame p1: "Dana Whitfield, Executive Director · decided Mar 12, 2026"): one person
   * with their position, two by name, more as the first and a count; the day is the newest decision
   * in the year, or "last decided" with the newest before it when the year holds none.
   */
  line15Summary: (people: readonly string[], day: string, earlier: boolean) => {
    // "last decided Mar 12, 2025" kept on one line: a narrow column wraps after the person.
    const decided = `${earlier ? 'last\u00a0decided' : 'decided'}\u00a0${day}`;
    if (people.length <= 1) return `${people[0] ?? ''} · ${decided}`;
    if (people.length === 2) return `${people[0]} and ${people[1]} · ${decided}`;
    return `${people[0]} and ${people.length - 1} others · ${decided}`;
  },
  compensationHeading: 'Compensation decisions',
  documentsNamed: (count: number) =>
    count === 1 ? '1 document named' : `${count} documents named`,
  /** A person's decision placed from before the tax year: "the year last undertaken". */
  latestBeforeThisYear: 'The latest decision before this tax year',
  lineLabelNote: 'Each decision is on the line your organization labelled it with.',
  /** An index entry's record with no title of its own. */
  untitledRecord: 'A record in the minute book',
} as const;
